import deepEquals from "fast-deep-equal";
import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { estateFlowchartApiClient, ResponseStatus } from "src/EstateFlowchart/client/EstateFlowchartApiClient";
import { getNavItemPage } from "src/EstateFlowchart/components/FormNav/FormNavUtils";
import { EstateFlowchartState, FiduciarySummary, WillModalInputs } from "src/EstateFlowchart/model/EstateFlowchartTypes";
import {
    familyMembersSelector, getEstateFlowchartsSelector,
    getSelectedFlowchart,
    setGenericErrorModal,
    setSelectedFlowchartId,
    updateEstateFlowchart
} from "src/EstateFlowchart/slices/EstateFlowChartSlice";
import { navItemSelector, setSaveClicked } from "src/EstateFlowchart/slices/NavigationSlice";
import { validateForm } from "src/EstateFlowchart/validation/form/WillFormValidator";
import { ValidationError } from "src/EstateFlowchart/validation/ValidationTypes";
import { RouteWithIdAndFlowchartId } from "src/routes/types";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { selectReleaseToggles } from "../../../ReleaseToggles/releaseTogglesSlice";
import useAnalytics from "../../../utils/useAnalytics";
import { EstateCardViewModeType } from "../../components/EstateCard/EstateCard";
import Form from "../../components/Form/Form";
import { WillFormPages } from "../../config/WillFormPages";
import { Will } from "../../model/Will";
import { emptyWill, getMemberNameById } from "../../utils/DefUtils";
import { getFiduciaries, handleSaveFiduciaries, initializeFiduciaryState } from "../../utils/FiduciaryUtils";
import { EstateItemType } from "../EstateItemType";
import WillContent from "./WillContent";

const WillForm = () => {
    const pageConfig = WillFormPages;
    const selectedFlowchart = useAppSelector(getSelectedFlowchart);
    const activeNavItem = useAppSelector(navItemSelector);
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
    const location = useLocation();
    const dispatch = useAppDispatch();
    const {id, flowchartId, trustId} = useParams<RouteWithIdAndFlowchartId>();
    const releaseToggles = useAppSelector(selectReleaseToggles);
    const [initialModalData, setInitialModalData] = useState<WillModalInputs>();
    const initialWill: Will = React.useMemo(() => {
    const {primaryWill, partnerWill} = selectedFlowchart?.willSummary || {};
        return (primaryWill?.trustId === trustId && {...primaryWill, flowchartName: selectedFlowchart?.flowchartName ?? ""}) ||
            (partnerWill?.trustId === trustId && {...partnerWill, flowchartName: selectedFlowchart?.flowchartName ?? ""}) ||
            {
                ...emptyWill,
                flowchartName: initialModalData?.flowchartName ?? selectedFlowchart?.flowchartName ?? "",
                willOwner: initialModalData?.willOwner ?? "",
                pourOverWill: true,
                primaryWill: initialModalData?.willOwner === primaryWill?.willOwner
            };
    }, [selectedFlowchart, trustId, initialModalData]);
    const [will, setWill] = useState<Will>({
        ...initialWill,
        flowchartName: selectedFlowchart?.flowchartName ?? initialWill.flowchartName
    });
    const [formHasBeenEdited, setFormHasBeenEdited] = useState(false);
    const familyMembers = useAppSelector(familyMembersSelector);
    const [willOwnerName, setWillOwnerName] = useState<string>("");
    const initialWillOwnerName = getMemberNameById(familyMembers, will.willOwner);

    const [fiduciaries, setFiduciaries] = useState<FiduciarySummary>({} as FiduciarySummary);
    const [initialFiduciaryState, setInitialFiduciaryState] = useState<FiduciarySummary>(() =>
        initializeFiduciaryState(familyMembers)
    );
    const flowcharts = useAppSelector(getEstateFlowchartsSelector);

    useEffect(() => {
        getFiduciaries(id, initialFiduciaryState, setFiduciaries, dispatch, setInitialFiduciaryState);

        setWillOwnerName(getMemberNameById(familyMembers, will.willOwner) ?? "");
        if(will.customWillOwner && !will.primaryWill) {
            setWillOwnerName(will.customWillOwner);
        }

    }, [id, dispatch]);

    useEffect(() => {
        let willToCompare = {...will};
        if (flowchartId === "new") {
            willToCompare = {
                ...willToCompare,
                flowchartName: ""
            };
        }
        const hasBeenEdited: boolean = !deepEquals(initialWill,willToCompare);
        setFormHasBeenEdited(hasBeenEdited);
    }, [will, initialWill]);

    useEffect(() => {
        const flowchartModelData = location.state;
        if (flowchartModelData?.estateItem) {
            setInitialModalData(flowchartModelData);
            setWill({
                ...will,
                flowchartName: flowchartModelData.flowchartName || selectedFlowchart?.flowchartName,
                willOwner: flowchartModelData.willOwner,
                pourOverWill: true,
                primaryWill: familyMembers.primaryMember.id === flowchartModelData.willOwner
            })
        }
    }, [location.state]);

    useAnalytics(
        "DEF_Will_Form",
        "DEF_Will_Form_TimeSpent",
    );

    const setCommunicationError = (errorStatus: ResponseStatus) => {
        dispatch(setGenericErrorModal({
            isOpen: true,
            operationId: errorStatus.traceId
        }));
    }

    const checkFormIsValid = () => {
        const pageId = getNavItemPage(activeNavItem)?.id;
        const {formHasErrors, errors} = validateForm(pageId, will, flowcharts, flowchartId, releaseToggles?.enableEstateFlowchartMVPRelease ?? false);
        setValidationErrors(errors);
        return !formHasErrors;
    }

    const setWillSummaryFromResponse = (response: ResponseStatus) => {
        const updatedWillSummary = {
            ...selectedFlowchart?.willSummary,
            primaryWill: will.primaryWill ? response.data : selectedFlowchart?.willSummary?.primaryWill,
            partnerWill: will.primaryWill ? selectedFlowchart?.willSummary?.partnerWill : response.data
        };

        dispatch(updateEstateFlowchart({
            ...selectedFlowchart,
            flowchartName: (response as ResponseStatus).data.flowchartName,
            willSummary: updatedWillSummary
        } as EstateFlowchartState));
        return true;
    }

    const createNewWillAndFlowchart = async (): Promise<boolean> => {
        const estateFlowchart: EstateFlowchartState = {
            flowchartId: undefined,
            flowchartName: will.flowchartName || "",
            revocableTrust: undefined,
            willSummary: {
                primaryWill: will.primaryWill ? will : undefined,
                partnerWill: will.primaryWill ? undefined : will
            },
            viewMode: EstateCardViewModeType.SIMPLE,
            estateTaxDueTypes: []
        };

        await estateFlowchartApiClient.createFlowchart(id, estateFlowchart)
            .then((response: ResponseStatus) => {
                if (response.isSuccessStatusCode) {
                    dispatch(setSelectedFlowchartId(response.data.flowchartId));
                    dispatch(updateEstateFlowchart(response.data));
                    return true;
                } else {
                    setCommunicationError(response);
                }
            });
        return false;
    };

    const addWillToTheFlowchart = async (): Promise<boolean> => {
        if (flowchartId) {
            await estateFlowchartApiClient.createWill(id, flowchartId, will)
                .then((response: ResponseStatus) => {
                    if (response.isSuccessStatusCode) {
                        return setWillSummaryFromResponse(response);
                    } else {
                        setCommunicationError(response);
                    }
                });
        }
        return false;
    };

    const handleEditWill = async (): Promise<boolean> => {
        if (formHasBeenEdited) {
            await estateFlowchartApiClient.editWill(id, flowchartId, will)
                .then((response: ResponseStatus) => {
                    if (response.isSuccessStatusCode) {
                        return setWillSummaryFromResponse(response);
                    } else {
                        setCommunicationError(response);
                    }
                });
        }
        return false;
    };

    const saveForm = async (): Promise<boolean> => {
        dispatch(setSaveClicked(true));
        let handleSave: () => Promise<boolean>;
        if (flowchartId === "new") {
            handleSave = createNewWillAndFlowchart;
        } else if (trustId === "new") {
            handleSave = addWillToTheFlowchart;
        } else {
            handleSave = handleEditWill;
        }
        await handleSaveFiduciaries(id, fiduciaries, dispatch, false);
        return handleSave();
    };

    return (
        <Form estateItem={will}
              title={`${initialWillOwnerName ?? willOwnerName}'s ${EstateItemType.WILL.valueOf()}`}
              estateItemType={EstateItemType.WILL}
              subHeader={will.flowchartName ?? undefined}
              pageConfig={pageConfig}
              isFormChanged={formHasBeenEdited}
              validate={checkFormIsValid}
              saveForm={saveForm}
              validationErrors={validationErrors}>
            <WillContent
                will={will}
                updateWill={setWill}
                pageConfig={pageConfig}
                fiduciaries={fiduciaries}
                updateFiduciaries={setFiduciaries}
            />
        </Form>
    );
}

export default WillForm;
